import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Button, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { NoPermission } from '../../../components/NoPermission';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { ResourceCode } from '../../../constants/resourceCodes';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { GET_WAREHOUSE_BY_ID, GetWarehouseByIdResponse } from '../../../graphql/queries/warehouseQueries';
import { hasResource } from '../../../utils/resourceAuth';
import { translateGraphQLError } from '../../../utils/graphqlUtils';
import { warehouseLocalDate } from '../../../utils/parcelSlaMonitoringLink';
import DateTimeRangeFilterItem from '../../delivery/components/DateTimeRangeFilterItem';
import BatchColumnLabel from '../batchPush/components/BatchColumnLabel';
import { formatNumber } from '../batchPush/batchPushUtils';
import {
    BatchPushHistoryBatch,
    BatchPushHistoryItem,
    PAGE_BATCH_PUSH_HISTORY,
    PageBatchPushHistoryResponse,
    PageBatchPushHistoryVariables,
} from './graphql/pageBatchPushHistory';

const DEFAULT_PAGE_SIZE = 10;

/** 推送时间展示：YYYY-MM-DD HH:mm（仓库当地时间） */
const formatPushedAt = (value: string | null): string => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-');

/**
 * 表格行：一次推送（push）或其下的一个批次（batch）。多批次推送的批次作为树形子行，与推送行共用同一组列
 * （推送时间、推送人、推送批次、推送包裹数），上下对齐；批次行的推送时间、推送人取所属推送。
 */
type HistoryRow =
    | { kind: 'push'; key: string; push: BatchPushHistoryItem; children?: HistoryRow[] }
    | { kind: 'batch'; key: string; push: BatchPushHistoryItem; batch: BatchPushHistoryBatch };

/** 推送记录 → 表格行；只有多批次的推送才挂批次子行（单批次直接在推送行展示批次）。 */
const toRows = (items: BatchPushHistoryItem[]): HistoryRow[] =>
    items.map((push) => {
        const children = push.childList ?? [];
        const key = String(push.pushRecordId);
        return children.length > 1
            ? {
                kind: 'push',
                key,
                push,
                children: children.map((batch, idx) => ({ kind: 'batch', key: `${key}_${batch.wmsBatchNumber ?? idx}`, push, batch })),
            }
            : { kind: 'push', key, push };
    });

/**
 * 分批次-尾程「批次推送历史」页：参照线路历史——推送日期范围默认当日、服务端分页、父子行展开。
 * 父行是一次推送（推送时间、推送人、推送批次、推送包裹数），子行是该次推送的批次；只展示成功记录。
 *
 * 「当日」按当前仓的本地日期（仓库 timezoneLocation）计算，不用浏览器日期：推送时间是仓库当地时间，
 * 浏览器与仓库跨日时（如北京上午 = 美东前一天晚上）用浏览器日期会查到仓库的「明天」。
 * WMS 侧 WarehouseContext 的仓库列表不返回时区，按 id 查 getWarehouseById；时区取不到时才回落浏览器日期。
 */
const BatchPushHistoryPage: React.FC = () => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const permitted = hasResource([ResourceCode.menu.WHS_DELIVERY_BATCH_PUSH_HISTORY]);
    const warehouseId = Number(currentWarehouse?.id ?? 0);

    const { data: warehouseData, loading: warehouseLoading } = useQuery<GetWarehouseByIdResponse>(GET_WAREHOUSE_BY_ID, {
        variables: { id: warehouseId },
        skip: !permitted || !warehouseId,
    });
    // 仓库当地当天：每次渲染重新计算，页面跨过仓库当地零点后，未自选日期的默认值 / 重置随之更新
    const localDate = warehouseLocalDate(warehouseData?.warehouse?.timezoneLocation);
    const warehouseToday = (localDate ? dayjs(localDate) : dayjs()).startOf('day');
    // null 表示未自选日期，取仓库当地当天（切仓后随新仓的时区变化）
    const [draftRange, setDraftRange] = useState<[Dayjs, Dayjs] | null>(null);
    const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
    const effectiveDraft: [Dayjs, Dayjs] = draftRange ?? [warehouseToday, warehouseToday];
    const effectiveRange: [Dayjs, Dayjs] = range ?? [warehouseToday, warehouseToday];
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

    const { data, loading } = useQuery<PageBatchPushHistoryResponse, PageBatchPushHistoryVariables>(PAGE_BATCH_PUSH_HISTORY, {
        variables: {
            request: {
                warehouseId,
                startDate: effectiveRange[0].format('YYYY-MM-DD'),
                endDate: effectiveRange[1].format('YYYY-MM-DD'),
                pageNum,
                pageSize,
            },
        },
        // 等仓库时区查完再查历史，避免先按浏览器日期查一次
        skip: !permitted || !warehouseId || warehouseLoading,
        fetchPolicy: 'network-only',
        onError: (e) => message.error(translateGraphQLError(e, 'wms.batchPush.messages.operationFailed')),
    });
    const history = data?.history;
    const rows = useMemo(() => toRows(history?.content ?? []), [history]);

    const toggleExpand = useCallback((key: string) => {
        setExpandedRowKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    }, []);

    const columns: ColumnsType<HistoryRow> = [
        // 展开图标列：多批次推送行显示 +/−
        { key: 'expand', width: 48, render: () => null },
        {
            title: t('wms.batchPushHistory.columns.pushedAt'),
            key: 'pushedAt',
            width: 200,
            render: (_: unknown, r: HistoryRow) => formatPushedAt(r.push.pushedAt),
        },
        {
            title: t('wms.batchPushHistory.columns.operator'),
            key: 'operatorName',
            // 加宽推送人列，账号名（如 HEADQUARTERS_STAFF）不再折行；超长省略并悬停显示全称
            width: 260,
            ellipsis: true,
            render: (_: unknown, r: HistoryRow) => r.push.operatorName || '-',
        },
        {
            title: t('wms.batchPushHistory.columns.batches'),
            key: 'batches',
            // 推送批次列定宽，不再独占剩余宽度（原先与推送包裹数之间留出大块空白）；剩余宽度给最后一列
            width: 320,
            render: (_: unknown, r: HistoryRow) => {
                if (r.kind === 'batch') {
                    return <BatchColumnLabel column={r.batch} />;
                }
                const children = r.push.childList ?? [];
                if (children.length === 1) {
                    return <BatchColumnLabel column={children[0]} />;
                }
                // 多批次：「X 个批次」链接与左侧 +/− 展开图标共用展开状态，点任一处展开 / 收起批次明细
                return (
                    <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => toggleExpand(r.key)}>
                        {t('wms.batchPushHistory.batchCount', { count: r.push.batchCount ?? children.length })}
                    </Button>
                );
            },
        },
        {
            title: t('wms.batchPushHistory.columns.parcelCount'),
            key: 'parcelCount',
            render: (_: unknown, r: HistoryRow) => formatNumber(r.kind === 'batch' ? r.batch.parcelCount : r.push.parcelCount),
        },
    ];

    if (!permitted) return <NoPermission />;

    return (
        <div style={{ padding: 24 }}>
            <div className="flex items-center mb-4">
                <div className="border-l-4 border-indigo-600 pl-2 text-xl font-medium">{t('wms.batchPushHistory.title')}</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                <style>{`.batch-push-history-child-row td { background: #f8f8fa !important; }`}</style>
                <div className="flex items-center gap-4 py-3">
                    <div style={{ width: 420 }}>
                        <DateTimeRangeFilterItem
                            label={String(t('wms.batchPushHistory.dateRange'))}
                            value={[effectiveDraft[0].format('YYYY-MM-DD'), effectiveDraft[1].format('YYYY-MM-DD')]}
                            onChange={(v) => {
                                if (v?.[0] && v?.[1]) setDraftRange([dayjs(v[0]).startOf('day'), dayjs(v[1]).startOf('day')]);
                            }}
                            format="YYYY-MM-DD"
                            showTime={false}
                            allowClear={false}
                        />
                    </div>
                    <span className="flex-1" />
                    <Button
                        className="min-w-24"
                        onClick={() => {
                            setDraftRange(null);
                            setRange(null);
                            setPageNum(1);
                            setPageSize(DEFAULT_PAGE_SIZE);
                        }}
                    >
                        <span className="font-bold">{t('common.reset')}</span>
                    </Button>
                    <Button
                        type="primary"
                        className="min-w-24"
                        onClick={() => {
                            setRange(effectiveDraft);
                            setPageNum(1);
                        }}
                    >
                        <span className="font-bold">{t('common.search')}</span>
                    </Button>
                </div>
                <Table<HistoryRow>
                    rowKey="key"
                    columns={columns}
                    dataSource={rows}
                    loading={loading}
                    pagination={false}
                    tableLayout="fixed"
                    rowClassName={(r) => (r.kind === 'batch' ? 'batch-push-history-child-row' : '')}
                    expandable={{
                        // 树形子行：批次行与推送行共用列；展开图标放在第一列，子行不缩进
                        expandedRowKeys,
                        onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
                        expandIconColumnIndex: 0,
                        indentSize: 0,
                    }}
                />
                <div style={{ marginTop: 16 }}>
                    {/* 合计（PRD：共推送 X 次、共推送包裹数）替换分页栏默认的「合计：X条」，只保留一行；条数即推送次数 */}
                    <TablePaginationBar
                        totalText={
                            <span className="text-gray-600" data-testid="history-total">
                                {t('wms.batchPushHistory.total', {
                                    pushCount: formatNumber(history?.totalPushCount),
                                    parcelCount: formatNumber(history?.totalParcelCount),
                                })}
                            </span>
                        }
                        total={Number(history?.total ?? 0)}
                        current={pageNum}
                        pageSize={pageSize}
                        onChange={(p, s) => {
                            setPageNum(p);
                            setPageSize(s);
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default BatchPushHistoryPage;
